import path from "node:path";
import { EventEmitter } from "node:events";
import process from "node:process";
import { AutoOnEvent, SecugenErrors, UnsupportedPlatformPayload, } from "./types";
const bindings = require("node-gyp-build")(path.join(__dirname, ".."));
const SecugenHandlerModule = bindings;
let _moduleInstance;
let _emitter = new EventEmitter();
let _autoOnCapturePromise = null;
let _autoOnCapturePromiseResolve = null;
const _onceListeners = new Map();
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
process.on("SIGINT", () => {
    if (_moduleInstance) {
        _moduleInstance.closeDevice();
    }
    process.exit();
});
export class SecugenHandler {
    constructor() {
        // if not on windows, do not initialize module
        if (process.platform !== "win32") {
            return;
        }
        if (_moduleInstance) {
            return;
        }
        try {
            _moduleInstance = SecugenHandlerModule;
        }
        catch (err) {
            console.log(err);
        }
    }
    /**
     * @description
     * Start the auto-on mode of the fingerprint scanner, thus allowing the
     * scanner to capture fingerprints automatically and emit events.
     */
    async startAutoOn() {
        if (!_moduleInstance) {
            return UnsupportedPlatformPayload;
        }
        _moduleInstance.startAutoOn((event, ...args) => {
            _emitter.emit(event, ...args);
        });
        await delay(100);
        return { ok: true };
    }
    /**
     * @description
     * Stop the auto-on mode of the fingerprint scanner, thus stopping the
     * scanner from capturing fingerprints automatically and emitting events.
     */
    stopAutoOn() {
        if (!_moduleInstance) {
            return UnsupportedPlatformPayload;
        }
        _moduleInstance.stopAutoOn();
        return { ok: true };
    }
    /**
     * @description
     * Subscribe to an auto-on event emitted by the fingerprint scanner.
     */
    subscribeToAutoOnEvent(event, listener) {
        if (!_moduleInstance) {
            return UnsupportedPlatformPayload;
        }
        _emitter.on(event, listener);
        return { ok: true };
    }
    /**
     * @description
     * Unsubscribe from an auto-on event emitted by the fingerprint
     * scanner.
     */
    unsubscribeFromAutoOnEvent(event, listener) {
        if (!_moduleInstance) {
            return UnsupportedPlatformPayload;
        }
        _emitter.off(event, listener);
        return { ok: true };
    }
    /**
     * @description
     * Get the device information of the fingerprint scanner.
     */
    getDeviceInfo() {
        if (!_moduleInstance) {
            return UnsupportedPlatformPayload;
        }
        try {
            return _moduleInstance.getDeviceInfo();
        }
        catch (err) {
            const parsedError = JSON.parse(err);
            return {
                ok: false,
                code: parsedError.code,
                message: parsedError.message,
            };
        }
    }
    /**
     * @description
     * Open the fingerprint scanner device.
     */
    open() {
        if (!_moduleInstance) {
            return UnsupportedPlatformPayload;
        }
        return _moduleInstance.openDevice();
    }
    /**
     * @description
     * Capture and create a template from the fingerprint scanner.
     *
     * @param {number} timeout The timeout in milliseconds.
     */
    async captureAndCreateTemplate(timeout) {
        if (!_moduleInstance) {
            return UnsupportedPlatformPayload;
        }
        return new Promise((resolve, reject) => {
            try {
                _moduleInstance.captureAndCreateTemplate((err, data) => {
                    if (err) {
                        resolve(err);
                    }
                    resolve(data);
                }, timeout);
            }
            catch (err) {
                reject({
                    ok: false,
                    code: "MISC_ERROR",
                    message: err,
                });
            }
        });
    }
    /**
     * @description
     * Capture a fingerprint from the auto-on mode of the fingerprint scanner.
     * This method will return a promise that resolves with the captured
     * fingerprint data. Accepts an optional timeout parameter that will reject
     * the promise if the timeout is reached.
     *
     * @param {number} [timeout] The timeout in milliseconds.
     */
    async captureFromAutoOn(timeout) {
        if (!_moduleInstance) {
            return UnsupportedPlatformPayload;
        }
        if (_moduleInstance.isAutoOnActive() === false) {
            return {
                ok: false,
                code: "AUTO_ON_NOT_ACTIVE",
                message: SecugenErrors.AUTO_ON_NOT_ACTIVE,
            };
        }
        if (_autoOnCapturePromise != null) {
            return _autoOnCapturePromise;
        }
        const promise = new Promise((resolve) => {
            _autoOnCapturePromiseResolve = resolve;
            const autoOnCaptureListener = (data) => {
                this.clearAutoOnCapturePromise();
                resolve(data);
            };
            const autoOnErrorListener = (error) => {
                this.clearAutoOnCapturePromise();
                resolve({
                    ok: false,
                    code: "MISC_ERROR",
                    message: error,
                });
            };
            _onceListeners.set(AutoOnEvent.Capture, autoOnCaptureListener);
            _onceListeners.set(AutoOnEvent.SystemError, autoOnErrorListener);
            _emitter.once(AutoOnEvent.Capture, autoOnCaptureListener);
            _emitter.once(AutoOnEvent.SystemError, autoOnErrorListener);
            if (timeout) {
                setTimeout(() => {
                    this.clearAutoOnCapturePromise();
                    resolve({
                        ok: false,
                        code: "CAPTURE_TIMEOUT",
                        message: SecugenErrors.CAPTURE_TIMEOUT,
                    });
                }, timeout);
            }
        });
        _autoOnCapturePromise = promise;
        return promise;
    }
    /**
     * @description
     * Cancel the capture from the auto-on mode of the fingerprint scanner.
     * This method will reject the promise returned by the `captureFromAutoOn`
     */
    async cancelCaptureFromAutoOn() {
        if (!_moduleInstance) {
            return UnsupportedPlatformPayload;
        }
        if (_autoOnCapturePromise != null) {
            _autoOnCapturePromiseResolve?.({
                ok: false,
                code: "CAPTURE_CANCELLED",
                message: SecugenErrors.CAPTURE_CANCELLED,
            });
            this.clearAutoOnCapturePromise();
        }
        return { ok: true };
    }
    /**
     * @description
     * Close the fingerprint scanner device. This method will also clear the
     * promise returned by the `captureFromAutoOn` method.
     */
    async close() {
        if (!_moduleInstance) {
            return UnsupportedPlatformPayload;
        }
        if (_autoOnCapturePromise != null) {
            _autoOnCapturePromiseResolve?.({
                ok: false,
                code: "CAPTURE_CANCELLED",
                message: SecugenErrors.CAPTURE_CANCELLED,
            });
            this.clearAutoOnCapturePromise();
        }
        return await delay(1000).then(() => {
            return _moduleInstance.closeDevice();
        });
    }
    /**
     * @description
     * Compare two fingerprint templates and return the result.
     * @param template1 - Base64 encoded template 1
     * @param template2 - Base64 encoded template 2
     */
    compareTemplates(template1, template2) {
        if (!_moduleInstance) {
            return UnsupportedPlatformPayload;
        }
        return _moduleInstance.compareTemplates(template1, template2);
    }
    clearAutoOnCapturePromise() {
        _autoOnCapturePromise = null;
        _autoOnCapturePromiseResolve = null;
        if (_onceListeners.get(AutoOnEvent.Capture) != null) {
            _emitter.off(AutoOnEvent.Capture, _onceListeners.get(AutoOnEvent.Capture));
            _onceListeners.delete(AutoOnEvent.Capture);
        }
        if (_onceListeners.get(AutoOnEvent.SystemError) != null) {
            _emitter.off(AutoOnEvent.SystemError, _onceListeners.get(AutoOnEvent.SystemError));
            _onceListeners.delete(AutoOnEvent.SystemError);
        }
    }
}
